import React, { useState } from 'react';
import { MdEmail, MdBadge, MdBusiness, MdArrowBack, MdLock } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ROUTES } from '../constants/endpoints';
import { adminAPI } from '../services/adminAPI';
import '../styles/ProfilePage.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('Please fill all password fields.');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }

    try {
      setSavingPassword(true);
      await adminAPI.changePassword(passwordForm);
      setPasswordSuccess('Password updated successfully.');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      setPasswordError(error.response?.data?.message || 'Failed to update password.');
    } finally {
      setSavingPassword(false);
    }
  };

  const joinedDate = admin?.createdAt
    ? new Date(admin.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : 'Not available';

  return (
    <div className="profile-page">
      <button type="button" className="profile-back-btn" onClick={() => navigate(-1)}>
        <MdArrowBack />
        Back
      </button>

      <div className="profile-hero">
        <div className="profile-avatar">
          {admin?.name?.charAt(0)?.toUpperCase() || 'A'}
        </div>
        <div className="profile-hero-copy">
          <h1>{admin?.name || 'Admin Profile'}</h1>
          <p>{admin?.role || 'admin'}</p>
        </div>
      </div>

      <div className="profile-grid">
        <section className="profile-card">
          <h2>Account Details</h2>
          <div className="profile-row">
            <span><MdEmail /> Email</span>
            <strong>{admin?.email || 'Not set'}</strong>
          </div>
          <div className="profile-row">
            <span><MdBadge /> Role</span>
            <strong>{admin?.role || 'Not set'}</strong>
          </div>
          <div className="profile-row">
            <span><MdBusiness /> Department</span>
            <strong>{admin?.department || 'Not set'}</strong>
          </div>
        </section>

        <section className="profile-card">
          <h2>Activity</h2>
          <div className="profile-row">
            <span>Joined</span>
            <strong>{joinedDate}</strong>
          </div>
          <div className="profile-row">
            <span>User ID</span>
            <strong>{admin?.id || admin?._id || 'Not set'}</strong>
          </div>
          <div className="profile-actions">
            <button type="button" className="profile-secondary-btn" onClick={() => navigate(ROUTES.ADMIN_TEAMS)}>
              View Teams
            </button>
          </div>
        </section>

        <section className="profile-card profile-card--full">
          <h2>Change Password</h2>
          <form className="password-form" onSubmit={handlePasswordSubmit}>
            {passwordError && <p className="password-message password-message--error">{passwordError}</p>}
            {passwordSuccess && <p className="password-message password-message--success">{passwordSuccess}</p>}

            <label className="password-field">
              <span><MdLock /> Current Password</span>
              <input
                type="password"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                placeholder="Enter current password"
              />
            </label>

            <label className="password-field">
              <span><MdLock /> New Password</span>
              <input
                type="password"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                placeholder="Enter new password"
              />
            </label>

            <label className="password-field">
              <span><MdLock /> Confirm Password</span>
              <input
                type="password"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                placeholder="Confirm new password"
              />
            </label>

            <button type="submit" className="profile-secondary-btn" disabled={savingPassword}>
              {savingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default ProfilePage;
